import { useCallback, useEffect, useRef } from "react";
import {
  CanvasChunkType,
  makeCanvasChunk,
} from "@/canvas/agent/chunk_protocol";
import { sendSharedCanvasChunk } from "@/canvas/agent/shared-ws";
import { disposeEagerTransport } from "@/canvas/agent/eagerTransport";
import { CANVAS_GATEWAY_CONFIGURED } from "@/flags";
import { useNotepadStore } from "@/features/notepad/store/useNotepadStore";
import { isNotepadSurface } from "@/features/notepad/utils/teachingSurface";
import { useSessionStore } from "@/features/tutor/store/useSessionStore";
import { useSlideStore } from "@/features/tutor/store/useSlideStore";
import { useUIStore } from "@/features/tutor/store/useUIStore";
import { useTurnStore } from "@/features/tutor/store/useTurnStore.js";
import { handleLessonEnd } from "@/features/tutor/utils/endLesson";
import { markLessonEndedByTutor } from "@/features/tutor/utils/stopLessonSession";
import { executeSlideTransition } from "@/features/tutor/utils/slideTransition";
import { useRuntimeContext } from "@/features/tutor/context/RuntimeContext";
import { usePlayerTick } from "@/features/tutor/hooks/usePlayerTick";
import { useAuthStore } from "@/store/useAuthStore";
import { decodeAudioChunk } from "@/utils/audioUtils";
import { tutorApi } from "@/utils/axios-utils";
import { compress } from "@/utils/compression";
import { debugLog, debugWarn } from "@/utils/debugLog";
import { TutorMode } from "@/constants/mode";
import {
  applyAvailabilityOverride,
  useDebugStore,
} from "@/store/useDebugStore";

/**
 * Whether the slide offers a tool ("scratchpad" | "calculator"). Dev builds
 * only: the dev overlay can force it either way.
 */
const isSlideToolEnabled = (slide, tool, override) => {
  const isEnabled = slide?.metadata?.[tool]?.enabled === true;
  return __DEV_OVERLAY__
    ? applyAvailabilityOverride(isEnabled, override)
    : isEnabled;
};

export function useCanvasRuntimeBridge({
  canvasAccess,
  canvasAccessLessonId,
  clearCanvasCaptionsQueue,
  currentPageIndex,
  currentSlideIndex,
  doneChunkRecievedRef,
  emit,
  endInactivityTimer,
  getSlideByIndex,
  isCanvasOpen,
  isMobileAny,
  mode = TutorMode.PUBLIC,
  navigate,
  searchParams,
  setActiveAssessmentModal,
  setCanvasAccess,
  setIsAwaitingTutorResponseRef,
  setIsCalculatorAvailable,
  setIsScratchPadAvailable,
  setIsScratchPadOpen,
  setSearchParams,
  slides,
  subscribe,
}) {
  const { pcmPlayerRef } = useRuntimeContext();
  // Subscribed, so a changed override re-runs the slide effects below at once.
  // A constant in prod builds, so nothing re-renders there.
  const scratchpadOverride = useDebugStore((s) =>
    __DEV_OVERLAY__ ? s.scratchpadOverride : "default",
  );
  const calculatorOverride = useDebugStore((s) =>
    __DEV_OVERLAY__ ? s.calculatorOverride : "default",
  );
  const { enqueue: enqueueNotepadAction, cancelPending: cancelNotepadActions } =
    usePlayerTick(pcmPlayerRef);

  const wasCanvasOpenRef = useRef(false);
  const prevPageIndexRef = useRef(currentPageIndex);
  const notepadClosedPendingRef = useRef(false);
  const notepadClosedIngressRef = useRef(null);
  const slideChangedRef = useRef(false);
  const isCanvasOpenRef = useRef(isCanvasOpen);
  const canvasHistoryGetterRef = useRef(null);
  const prevSlideIndexRef = useRef(currentSlideIndex);
  const canvasStreamingRef = useRef(false);
  // Terminal notepad actions (next_slide / end_lesson) are scheduled against
  // the turn's audio clock and so can be pending for the length of the
  // remaining narration. They must not survive the turn that authored them: a
  // barge-in or a slide change would otherwise navigate out from under the
  // student mid-sentence.
  useEffect(() => {
    if (!isCanvasOpen) cancelNotepadActions();
  }, [isCanvasOpen, cancelNotepadActions]);

  const handleRegisterCanvasHistoryGetter = useCallback((getter) => {
    canvasHistoryGetterRef.current = getter;
  }, []);

  useEffect(() => {
    isCanvasOpenRef.current = isCanvasOpen;
  }, [isCanvasOpen]);

  useEffect(() => {
    if (!CANVAS_GATEWAY_CONFIGURED) {
      setCanvasAccess({
        enabled: false,
        allowed: false,
        reason: "gateway_not_configured",
      });
      return;
    }

    if (!canvasAccessLessonId) {
      setCanvasAccess({
        enabled: false,
        allowed: false,
        reason: "missing_lesson_id",
      });
      return;
    }

    let cancelled = false;

    tutorApi
      .get(`/session/canvas_access/${canvasAccessLessonId}`)
      .then((res) => {
        if (cancelled) return;
        const { enabled, allowed, reason, limitNotepadToOncePerSlide } =
          res.data;
        setCanvasAccess({
          enabled: !!enabled,
          allowed: !!allowed,
          reason: reason || "backend",
          limitNotepadToOncePerSlide: !!limitNotepadToOncePerSlide,
        });
        useNotepadStore.getState().setLimitedMode(!!limitNotepadToOncePerSlide);
      })
      .catch((err) => {
        if (cancelled) return;
        debugWarn("[Canvas] canvas_access check failed, disabling", err);
        setCanvasAccess({
          enabled: false,
          allowed: false,
          reason: "access_check_failed",
        });
      });

    return () => {
      cancelled = true;
    };
  }, [canvasAccessLessonId, setCanvasAccess]);

  useEffect(() => {
    const unsub = useNotepadStore.subscribe((curr, prev) => {
      if (curr.state === "OPEN_AI_ACTIVE" && prev.state === "TOAST_VISIBLE") {
        emit("NOTEPAD_OPENED");
      }

      // Notepad bite: the canvas IS the teaching surface, opened without the
      // toast, so it needs its own NOTEPAD_OPENED for the backend's notepad
      // clock (and the notepad_used checkpoint flag). Bite-to-bite navigation
      // keeps the state at OPEN_NOTEPAD_BITE, so this fires once per entry;
      // the matching NOTEPAD_CLOSED goes through the normal pending-close
      // path when the state returns to CLOSED. Student review stays silent —
      // no agent, tutor-surface time by design.
      if (
        curr.state === "OPEN_NOTEPAD_BITE" &&
        prev.state !== "OPEN_NOTEPAD_BITE"
      ) {
        emit("NOTEPAD_OPENED");
      }

      if (curr.state === "CLOSED" && prev.state !== "CLOSED") {
        if (prev.state !== "TOAST_VISIBLE") {
          notepadClosedPendingRef.current = true;
          notepadClosedIngressRef.current = prev.ingressType;
        } else if (slideChangedRef.current) {
          slideChangedRef.current = false;
        } else {
          emit("TURN_CHANGE");
          emit("NOTEPAD_NOT_OPENED");
        }
      }

      if (curr.state === "CLOSED" && prev.state !== "CLOSED") {
        canvasStreamingRef.current = false;
      }

      if (
        curr.ingressType === "ai" &&
        prev.state === "TOAST_VISIBLE" &&
        curr.state === "OPEN_AI_ACTIVE"
      ) {
        if (window.animationController) {
          const ctrl = window.animationController;
          ctrl.state = { value: 1, start: ctrl.clock.getElapsedTime() };
          ctrl.call("LISTEN");
          setTimeout(() => {
            if (window.animationController) {
              window.animationController.call("SPLIT");
            }
          }, 500);
        }
      }
    });
    return unsub;
  }, [emit]);

  useEffect(() => {
    let isFirstAudioChunk = true;

    const resetFirstChunk = () => {
      isFirstAudioChunk = true;
      window.__canvasAudioStreamStartCtxTime = null;
      window.__canvasStreamStartWallClock = null;
      window.__canvasPlayedMsAtStart = null;
      // A new turn invalidates the previous turn's audio clock, and with it
      // anything still scheduled against it.
      cancelNotepadActions();
      // Clear the done flag so the previous turn's lingering onended callback
      // doesn't fire AUDIO_PLAYBACK_ENDED and set audioEndedRef=true mid-turn.
      doneChunkRecievedRef.current = false;
    };
    window.addEventListener("canvas:turn-start", resetFirstChunk);

    const handler = (event) => {
      const chunk = event.detail;
      if (!isCanvasOpenRef.current) return;
      if (!window.pcmPlayer) return;
      const currentTurnId = useTurnStore.getState().getTurnId();
      if (
        typeof chunk?.turn_id === "string" &&
        chunk.turn_id.length > 0 &&
        currentTurnId &&
        chunk.turn_id !== currentTurnId
      ) {
        return;
      }
      try {
        const parsed =
          typeof chunk.chunk_data === "string"
            ? JSON.parse(chunk.chunk_data)
            : chunk.chunk_data;
        const b64 = parsed.audio_chunk_b64 ?? parsed;
        if (typeof b64 === "string" && b64.length > 0) {
          if (isFirstAudioChunk) {
            isFirstAudioChunk = false;
            const audioCtx = window.pcmPlayer.audioCtx;
            if (audioCtx) {
              const startCtxTime = Math.max(
                window.pcmPlayer.startTime,
                audioCtx.currentTime,
              );
              window.__canvasAudioStreamStartCtxTime = startCtxTime;
              // Wall-clock anchor every audio-relative timestamp in this turn
              // is measured from: op onsets, close lead time, next_slide.
              // startCtxTime can be ahead of currentTime when earlier audio is
              // still queued, so this is often slightly in the future.
              window.__canvasStreamStartWallClock =
                Date.now() + (startCtxTime - audioCtx.currentTime) * 1000;
            }
            const releaseAt = window.__notepadButtonReleaseWallClockMs;
            if (typeof releaseAt === "number" && releaseAt > 0) {
              const deltaMs = Date.now() - releaseAt;
              window.__notepadButtonReleaseWallClockMs = null;
              const turnId =
                (typeof chunk?.turn_id === "string" && chunk.turn_id) ||
                useTurnStore.getState().getTurnId() ||
                "";
              sendSharedCanvasChunk(
                CanvasChunkType.CANVAS_CLIENT_METRIC,
                JSON.stringify({
                  metric: "button_release_to_first_audio_ms",
                  value_ms: deltaMs,
                }),
                turnId,
              );
            }
            window.__canvasPlayedMsAtStart =
              pcmPlayerRef.current?.playedMs ?? 0;
          }
          window.pcmPlayer.feed(decodeAudioChunk(b64));
          // Canvas TTS bypasses the main WS, so the AUDIO_CHUNK handler that
          // normally clears isAwaitingTutorResponse never fires — unblock the
          // submit button here so the student can interrupt during playback.
          setIsAwaitingTutorResponseRef?.current?.(false);
        }
      } catch (err) {
        debugWarn("[Canvas Audio] Failed to decode CANVAS_AUDIO chunk", err);
      }
    };
    window.addEventListener("canvas:audio-chunk", handler);

    const doneHandler = () => {
      if (isCanvasOpenRef.current) {
        doneChunkRecievedRef.current = true;
      }
      // A turn that ended without canvas audio (error / cancel / text-only)
      // never consumed the button-release stamp — drop it so the next audio
      // turn can't measure latency from this turn's release. Keep stamps
      // younger than 2s: on an interrupt, the aborted turn's DONE can land
      // right after the user's fresh release for the next turn.
      const releaseAt = window.__notepadButtonReleaseWallClockMs;
      if (typeof releaseAt === "number" && Date.now() - releaseAt > 2000) {
        window.__notepadButtonReleaseWallClockMs = null;
      }
    };
    window.addEventListener("canvas:turn-done", doneHandler);

    return () => {
      window.removeEventListener("canvas:audio-chunk", handler);
      window.removeEventListener("canvas:turn-done", doneHandler);
      window.removeEventListener("canvas:turn-start", resetFirstChunk);
    };
  }, [
    cancelNotepadActions,
    doneChunkRecievedRef,
    setIsAwaitingTutorResponseRef,
  ]);

  useEffect(() => {
    const prev = prevPageIndexRef.current;
    prevPageIndexRef.current = currentPageIndex;
    if (prev === currentPageIndex) return;

    const { pages, ingressType } = useNotepadStore.getState();
    if (ingressType !== "ai" || pages.length <= 1) return;
    const latestPage = Math.max(...pages);
    if (prev === latestPage && currentPageIndex !== latestPage) {
      emit("TURN_CHANGE", { source: "page-switch" });
    }
  }, [currentPageIndex, emit]);

  useEffect(() => {
    if (!CANVAS_GATEWAY_CONFIGURED) return;

    const sendToCanvasWs = (data) => {
      const canvasWs = useNotepadStore.getState().notepadWs;
      if (!canvasWs || canvasWs.readyState !== WebSocket.OPEN) return false;
      const { data: compressed } = compress(JSON.stringify(data));
      canvasWs.send(compressed);
      return true;
    };

    const unsubAudio = subscribe("USER_AUDIO_CHUNK", (chunk_data) => {
      if (!isCanvasOpenRef.current) return;
      const base64String = btoa(
        String.fromCharCode(...new Uint8Array(chunk_data.buffer)),
      );
      const turnId = useTurnStore.getState().getTurnId() || "";
      const chunk = makeCanvasChunk(
        CanvasChunkType.CANVAS_AUDIO,
        JSON.stringify({ audio_chunk_b64: base64String }),
        turnId,
      );
      if (!sendToCanvasWs(chunk)) {
        debugWarn("[Canvas WS] Cannot send CANVAS_AUDIO - WebSocket not open");
      }
    });

    const unsubAudioDone = subscribe("USER_AUDIO_DONE", () => {
      debugLog(
        "[CanvasRuntimeBridge] USER_AUDIO_DONE received, isCanvasOpen:",
        isCanvasOpenRef.current,
      );
      if (!isCanvasOpenRef.current) {
        debugWarn(
          "[CanvasRuntimeBridge] USER_AUDIO_DONE skipped — isCanvasOpen is false",
        );
        return;
      }
      const turnId = useTurnStore.getState().getTurnId() || "";
      debugLog(
        "[CanvasRuntimeBridge] Sending CANVAS_AUDIO_DONE, turnId:",
        turnId,
      );
      const chunk = makeCanvasChunk(
        CanvasChunkType.CANVAS_AUDIO_DONE,
        "",
        turnId,
      );
      if (!sendToCanvasWs(chunk)) {
        debugWarn(
          "[Canvas WS] Cannot send CANVAS_AUDIO_DONE - WebSocket not open",
        );
      }
    });

    return () => {
      unsubAudio();
      unsubAudioDone();
    };
  }, [subscribe]);

  useEffect(() => {
    if (
      CANVAS_GATEWAY_CONFIGURED &&
      prevSlideIndexRef.current !== currentSlideIndex
    ) {
      slideChangedRef.current = true;
      disposeEagerTransport();
      if (!useNotepadStore.getState().isNotepadBite) {
        useNotepadStore.getState().dismissToast();
      }
      slideChangedRef.current = false;
    }
    prevSlideIndexRef.current = currentSlideIndex;
  }, [currentSlideIndex]);

  useEffect(() => {
    if (wasCanvasOpenRef.current !== isCanvasOpen) {
      endInactivityTimer();
      emit("TURN_CHANGE");
      if (!isCanvasOpen && notepadClosedPendingRef.current) {
        notepadClosedPendingRef.current = false;
        emit("NOTEPAD_CLOSED", {
          ingress_type: notepadClosedIngressRef.current || "ai",
        });
        notepadClosedIngressRef.current = null;
      }
    }
    wasCanvasOpenRef.current = isCanvasOpen;
  }, [endInactivityTimer, isCanvasOpen, emit]);

  // Auto-open the notepad for notepad-bite slides.
  //
  // Deliberately NOT gated on the session. The notepad IS the teaching surface
  // for these bites, so it opens as soon as the slide metadata says so —
  // before the student presses Start Learning, while the modal is still up.
  // That way the modal sits over the real surface and pressing Start Learning
  // does not tear a placeholder down and mount a canvas in its place
  // (ENGG-2434); the problem seeds itself into the canvas already on screen.
  //
  // Nothing runs early as a result: the gateway transport defers its connect
  // until `sessionId` exists (see WebSocketTransport), and the opening
  // "start teaching" turn needs a `turnId`, which only session boot sets.
  const enableNotepadBite = useAuthStore(
    (s) => !!s.user?.config?.enableNotepadBite,
  );
  // Re-open trigger.
  //
  // `sessionId` is a DEPENDENCY, not a gate. As a gate it used to stop the
  // notepad opening before the lesson started, which is what this PR needed to
  // change; as a dependency it still re-runs this effect whenever a session is
  // created, which is what reopens the canvas after Restart (the restart paths
  // close the notepad and bump the session nonce, and boot sets sessionId to
  // null and then to the new id).
  //
  // It deliberately does NOT watch the store's open flag. Doing that made the
  // effect fire on *every* close — including the one slideNavigationController
  // performs mid-transition, before `currentSlideIndex` has moved to the target
  // slide. The effect would see "closed, and the current slide is a notepad
  // bite", reopen, get closed again, and the canvas flickered.
  const sessionId = useSessionStore((s) => s.sessionId);
  const hasResolvedSlideIndex = useSlideStore((s) => s.hasResolvedSlideIndex);
  // Which slide index we last acted on. Without this, re-running on the flag
  // we just flipped would take the bite-to-bite branch and `addPage()` in a
  // loop.
  const openedForSlideRef = useRef(null);

  useEffect(() => {
    if (!CANVAS_GATEWAY_CONFIGURED) return;
    if (!enableNotepadBite) return;
    if (slides.length === 0 || currentSlideIndex === null) return;
    // Wait for a REAL slide index. `currentSlideIndex` starts at 0, which is
    // indistinguishable from genuinely being on slide 0, and the slides fetch
    // usually resolves before session discovery does.
    //
    // The `sessionId` gate this effect used to have was — accidentally — also
    // what guaranteed the index had settled, since a session only exists after
    // discovery. Removing it so the notepad could open pre-session meant the
    // notepad opened at the placeholder index instead: it keyed its canvas to
    // slide 0 and restored that slide's drawing on a mid-lesson refresh, then
    // read the correction to the real index as a bite-to-bite navigation and
    // added a page. Resume landed on a blank second page as a result.
    if (!hasResolvedSlideIndex) return;
    const currentSlide = getSlideByIndex(currentSlideIndex);
    const isNotepadBite = isNotepadSurface(currentSlide?.metadata);

    if (!isNotepadBite) {
      openedForSlideRef.current = null;
      return;
    }

    const notepadState = useNotepadStore.getState();
    if (notepadState.isNotepadBite) {
      // Already open and already handled for this slide — nothing to do.
      if (openedForSlideRef.current === currentSlideIndex) return;
      // Bite-to-bite: notepad stayed open, just add a new page
      notepadState.addPage();
    } else {
      // Notepad bites are always single-page (page 0). Unconditionally set
      // pages = [0] so that re-entry never creates a new page index, and
      // TldrawCanvasPanel will load the existing snapshot from sessionStorage
      // if one exists (canvas_state_${slideKey}-page-0).
      notepadState.restorePages([0]);
      notepadState.openForNotepadBite();
    }
    openedForSlideRef.current = currentSlideIndex;
  }, [
    currentSlideIndex,
    enableNotepadBite,
    getSlideByIndex,
    slides,
    sessionId,
    hasResolvedSlideIndex,
  ]);

  useEffect(() => {
    if (slides.length > 0 && currentSlideIndex !== null) {
      const scratchpadEnabled = isSlideToolEnabled(
        getSlideByIndex(currentSlideIndex),
        "scratchpad",
        scratchpadOverride,
      );
      setIsScratchPadAvailable(scratchpadEnabled);
      if (!isMobileAny) {
        setIsScratchPadOpen(scratchpadEnabled);
      } else if (!scratchpadEnabled) {
        setIsScratchPadOpen(false);
      }
    }
  }, [
    currentSlideIndex,
    getSlideByIndex,
    isMobileAny,
    scratchpadOverride,
    setIsScratchPadAvailable,
    setIsScratchPadOpen,
    slides,
  ]);

  useEffect(() => {
    if (slides.length > 0 && currentSlideIndex !== null) {
      const calculatorEnabled = isSlideToolEnabled(
        getSlideByIndex(currentSlideIndex),
        "calculator",
        calculatorOverride,
      );
      setIsCalculatorAvailable(calculatorEnabled);
      if (!calculatorEnabled) {
        useUIStore.getState().setIsCalculatorOpen(false);
      }
    }
  }, [
    calculatorOverride,
    currentSlideIndex,
    getSlideByIndex,
    setIsCalculatorAvailable,
    slides,
  ]);

  useEffect(() => {
    if (slides.length === 0 || currentSlideIndex === null) return;
    const scratchpadEnabled = isSlideToolEnabled(
      getSlideByIndex(currentSlideIndex),
      "scratchpad",
      scratchpadOverride,
    );
    if (scratchpadEnabled) {
      if (isCanvasOpen) {
        setIsScratchPadOpen(false);
      } else if (!isMobileAny) {
        setIsScratchPadOpen(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCanvasOpen, isMobileAny]);

  const isCanvasAvailable =
    CANVAS_GATEWAY_CONFIGURED && canvasAccess.enabled && canvasAccess.allowed;

  useEffect(() => {
    if (!isCanvasAvailable && isCanvasOpen) {
      if (useNotepadStore.getState().isNotepadBite) return;
      debugWarn("[Canvas] Closing because canvas is no longer available");
      useNotepadStore.getState().close();
    }
  }, [isCanvasAvailable, isCanvasOpen]);

  const handleCanvasStreamComplete = useCallback(() => {
    debugLog("[Canvas] Stream complete");
  }, []);

  const handleCanvasError = useCallback(
    (err) => {
      console.error("[Canvas] Error:", err);
      clearCanvasCaptionsQueue();
    },
    [clearCanvasCaptionsQueue],
  );

  const handleCanvasActionExecuted = useCallback((action) => {
    debugLog("[Canvas] Action executed:", action?._type);
    if (!action?.complete && !canvasStreamingRef.current) {
      canvasStreamingRef.current = true;
      window.animationController?.call("SPIRAL", { force: true });
    }
    if (action?.complete) {
      canvasStreamingRef.current = false;
    }
  }, []);

  // Notepad bite navigation handling (needs navigate/searchParams/emit from React)
  const navDepsRef = useRef({
    navigate,
    searchParams,
    setActiveAssessmentModal,
    setSearchParams,
    emit,
    mode,
  });
  navDepsRef.current = {
    navigate,
    searchParams,
    setActiveAssessmentModal,
    setSearchParams,
    emit,
    mode,
  };

  useEffect(() => {
    const handleNotepadNextSlide = (e) => {
      if (!useNotepadStore.getState().isNotepadBite) return;
      const timestamp =
        typeof e.detail?.timestamp === "number" ? e.detail.timestamp : 0;
      const targetMs = (window.__canvasPlayedMsAtStart ?? 0) + timestamp;

      enqueueNotepadAction(targetMs, async () => {
        const {
          emit,
          navigate,
          searchParams,
          setSearchParams,
          setActiveAssessmentModal,
          mode,
        } = navDepsRef.current;
        await executeSlideTransition({
          emit,
          navigate,
          searchParams,
          setSearchParams,
          setActiveAssessmentModal,
          persistSlideIndexInUrl: mode === TutorMode.PREVIEW,
        });
      });
    };

    const handleNotepadEndLesson = (e) => {
      if (!useNotepadStore.getState().isNotepadBite) return;
      const timestamp =
        typeof e.detail?.timestamp === "number" ? e.detail.timestamp : 0;
      const targetMs = (window.__canvasPlayedMsAtStart ?? 0) + timestamp;

      enqueueNotepadAction(targetMs, async () => {
        const { emit, navigate, searchParams } = navDepsRef.current;
        emit("LOCAL_INTERRUPT");
        markLessonEndedByTutor(useSlideStore.getState().currentSlideIndex);
        await handleLessonEnd(navigate, searchParams);
      });
    };

    window.addEventListener("notepad-bite:next-slide", handleNotepadNextSlide);
    window.addEventListener("notepad-bite:end-lesson", handleNotepadEndLesson);
    return () => {
      window.removeEventListener(
        "notepad-bite:next-slide",
        handleNotepadNextSlide,
      );
      window.removeEventListener(
        "notepad-bite:end-lesson",
        handleNotepadEndLesson,
      );
      cancelNotepadActions();
    };
  }, [enqueueNotepadAction, cancelNotepadActions]);

  return {
    canvasHistoryGetterRef,
    handleCanvasActionExecuted,
    handleCanvasError,
    handleCanvasStreamComplete,
    handleRegisterCanvasHistoryGetter,
    isCanvasAvailable,
    isCanvasOpenRef,
  };
}
